import { ComponentPropsWithoutRef, useCallback, useState } from 'react'
import {
  CheckIcon,
  ChevronRightIcon,
  PlusIcon,
} from '@heroicons/react/20/solid'
import { cn } from '~/utils/cn'
import { WorkspaceWithRelations } from './WorkspaceSwitch'
import { useDashboardWorkspaceContext } from '../dashboardContext'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '~/components/DropdownMenu'

type CreateWorkspaceItemProps = {
  onSelect?: (event: Event) => void
  type?: 'top-level' | 'child'
}

function CreateWorkspaceItem({
  onSelect,
  type = 'top-level',
}: CreateWorkspaceItemProps) {
  return (
    <DropdownMenuItem onSelect={onSelect}>
      <PlusIcon className={cn('w-3.5')} />
      <span>Create a {type === 'top-level' ? 'new' : 'child'} Workspace</span>
    </DropdownMenuItem>
  )
}

type WorkspaceSelectItemProps = {
  workspace: WorkspaceWithRelations
  onSelectItem?: (workspace: WorkspaceWithRelations) => void
  onCreateWorkspace?: (parentId?: string | null) => void
}
function WorkspaceSelectItem({
  workspace,
  onSelectItem,
  onCreateWorkspace,
}: WorkspaceSelectItemProps) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        onClick={() => onSelectItem?.(workspace)}
        className="group"
      >
        <div className="flex items-center gap-2">
          <CheckIcon
            className={cn('w-3.5 text-primary-500', {
              invisible: !workspace.selected,
            })}
          />
          <span>{workspace.commonName}</span>
        </div>
        <div className="ml-auto group-data-[disabled]:text-gray-400">
          <ChevronRightIcon className="w-4" />
        </div>
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent
          sideOffset={2}
          alignOffset={-5}
          footerContent={
            <CreateWorkspaceItem
              onSelect={() => onCreateWorkspace?.(workspace.id)}
              type="child"
            />
          }
          showSeparatorAboveFooter={!!workspace.children?.length}
        >
          {workspace.children?.map((workspace) => (
            <WorkspaceSelectItem
              key={workspace.id}
              workspace={workspace}
              onSelectItem={onSelectItem}
              onCreateWorkspace={onCreateWorkspace}
            />
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  )
}

type WorkspaceSelectProps = {
  workspaces: WorkspaceWithRelations[]
  selectedWorkspace?: WorkspaceWithRelations
  onSelectItem?: (workspace: WorkspaceWithRelations) => void
  onCreateWorkspace?: (parentId?: string | null) => void
}
export function WorkspaceSelect({
  workspaces,
  onSelectItem,
  onCreateWorkspace,
  selectedWorkspace,
}: WorkspaceSelectProps) {
  const [open, setOpen] = useState(false)
  const wsContext = useDashboardWorkspaceContext()
  const handleItemSelect = useCallback(
    (workspace: WorkspaceWithRelations) => {
      onSelectItem?.(workspace)
      setOpen(false)
    },
    [onSelectItem]
  )
  return (
    <DropdownMenuRoot open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center px-2.5 py-1.5 gap-3 rounded-full bg-white text-[#414141] outline-none"
          aria-label="Select workspace"
        >
          <span>{selectedWorkspace?.commonName ?? 'Select workspace'}</span>
          <ChevronDownIcon className="w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent
          sideOffset={5}
          footerContent={
            <CreateWorkspaceItem
              onSelect={() => onCreateWorkspace?.(wsContext.memberId)}
            />
          }
          showSeparatorAboveFooter={!!workspaces.length}
        >
          {workspaces.map((workspace) => (
            <WorkspaceSelectItem
              key={workspace.id}
              workspace={workspace}
              onSelectItem={handleItemSelect}
              onCreateWorkspace={onCreateWorkspace}
            />
          ))}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenuRoot>
  )
}

export function ChevronDownIcon(props: ComponentPropsWithoutRef<'svg'>) {
  return (
    <svg
      viewBox="0 0 16 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M15.5039 1.15625L14.8359 0.453125C14.6602 0.277344 14.3789 0.277344 14.2383 0.453125L7.875 6.81641L1.47656 0.453125C1.33594 0.277344 1.05469 0.277344 0.878906 0.453125L0.210938 1.15625C0.0351562 1.29688 0.0351562 1.57812 0.210938 1.75391L7.55859 9.10156C7.73438 9.27734 7.98047 9.27734 8.15625 9.10156L15.5039 1.75391C15.6797 1.57812 15.6797 1.29688 15.5039 1.15625Z"
        fill="currentColor"
      />
    </svg>
  )
}
